import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { PreEnrollmentTopBar } from 'features/layout/PreEnrollmentTopBar';
import { useReportLoading, useStartFlowHold } from 'features/loading';
import { useBlockFirstQuestionVital } from 'features/monitoring/useBlockFirstQuestionVital';
import { RoutePath } from 'features/routing/constants';
import { EmptyStatePage } from 'shared/components/EmptyStatePage';
import { assertExhaustiveMatch } from 'shared/utils/typeUtils';
import { resolveLandingView } from '../logic/resolveLandingView';
import { useEnrollment } from '../useEnrollment';
import { EnrollmentBody } from './EnrollmentBody';

type EnrollmentLandingProps = { userId: string; participationId: string };

const COMPLETE_KEYS = {
  title: 'EnrollmentCompleteTitle',
  subtitle: 'EnrollmentCompleteSubtitle',
};

const PRESCREEN_ONLY_COMPLETE_KEYS = {
  title: 'EnrollmentCompleteTitlePrescreenOnly',
  subtitle: 'EnrollmentCompleteSubtitlePrescreenOnly',
};

/** The pre-active home: enrollment progress + the phase questionnaire CTA.
 *  Holds on a spinner instead of the hero while the body is still resolving
 *  (composition loading, or a consenting participation's consent/complete
 *  split awaiting the schedule join); during the start flow the shared loading
 *  overlay holds instead. Closed and complete outcomes render the end page
 *  alone. A protocol that hides the landing forwards to the CTA target, behind
 *  the global loading screen while the seam settles. */
export const EnrollmentLanding = ({
  userId,
  participationId,
}: EnrollmentLandingProps) => {
  const { t } = useTranslation();
  const enrollment = useEnrollment({
    userId,
    participationId,
  });
  const { ctaRoute, isCtaResolving, isCtaResuming } = enrollment;
  const view = resolveLandingView(enrollment);
  const navigate = useNavigate();
  useReportLoading(view.kind === 'loading', {
    title: t('questionnaireInitializationLoadingTitle'),
    description: t('loadingScreenDoNotCloseMessage'),
  });
  const heldByStartFlow = useStartFlowHold(
    view.kind === 'forward' || (view.kind === 'landing' && !view.body)
  );
  useBlockFirstQuestionVital(
    view.kind === 'landing' && view.body !== undefined
  );

  switch (view.kind) {
    case 'error':
      return <Navigate to={RoutePath.SomethingWentWrong} replace />;

    case 'closed':
      return (
        <EmptyStatePage
          title={t('EnrollmentClosedTitle')}
          subtitle={t('EnrollmentClosedSubtitle')}
        />
      );

    case 'complete': {
      const keys =
        view.shape === 'prescreener-only'
          ? PRESCREEN_ONLY_COMPLETE_KEYS
          : COMPLETE_KEYS;
      return (
        <EmptyStatePage title={t(keys.title)} subtitle={t(keys.subtitle)} />
      );
    }

    case 'loading':
      return null;

    case 'forward':
      return <Navigate to={view.route} replace />;

    case 'landing':
      if (heldByStartFlow) return null;
      return (
        <>
          <PreEnrollmentTopBar />
          <Box
            component="main"
            aria-busy={!view.body || undefined}
            sx={{ px: 2, pt: { xs: 1, md: 3 }, pb: 4 }}
          >
            {view.body ? (
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <EnrollmentBody
                  phase={view.body.phase}
                  shape={view.body.shape}
                  steps={view.body.steps}
                  isCtaResolving={isCtaResolving}
                  isCtaResuming={isCtaResuming}
                  onCta={
                    ctaRoute != null ? () => void navigate(ctaRoute) : undefined
                  }
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                <CircularProgress
                  aria-label={t('questionnaireInitializationLoadingTitle')}
                />
              </Box>
            )}
          </Box>
        </>
      );

    default:
      return assertExhaustiveMatch(
        '[ASSERT FAILED] EnrollmentLanding: unhandled landing view',
        view
      );
  }
};
